import { Button } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import type { Editor } from '@tiptap/react';
import { Check, Loader2, X } from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import { useLayoutEffect } from 'react';
import { useIntl } from 'react-intl';
import { ComposerDictationWaveform } from './composer-dictation-waveform';
import { useComposerDictation } from './use-composer-dictation';

/**
 * The slice of the host's shared dictation session this row reads. Structural
 * on purpose — the store itself is the app's, and naming its type here would
 * drag the whole dictation domain through this door.
 */
export interface ComposerDictationSessionState {
  readonly status: string;
  readonly source: string | null;
  readonly editor: Editor | null;
  readonly stop: () => unknown;
  readonly cancel: () => void;
  readonly reset: () => void;
}

/**
 * The shared mic analyser the waveform samples on the web, reduced to the
 * members it touches.
 */
export interface ComposerDictationMicAnalyzer {
  readonly id: string;
  readonly analyzer: {
    fftSize: number;
    minDecibels: number;
    maxDecibels: number;
    smoothingTimeConstant: number;
    readonly frequencyBinCount: number;
    getByteFrequencyData: (frequencyData: Uint8Array) => void;
  };
}

/**
 * Everything the row needs that lives outside this package: the shared
 * dictation session, the native dictation bridge and the two audio sources the
 * waveform samples. Bound once at the composition point that owns them.
 */
export interface ComposerDictationPorts {
  /** Read one field of the host's shared dictation session store. */
  readonly useDictationStore: <T>(
    selector: (state: ComposerDictationSessionState) => T
  ) => T;
  /** True on the desktop host, where stop / cancel go through the native bridge. */
  readonly isNativeHost: () => boolean;
  readonly stopNativeDictation: () => Promise<unknown>;
  readonly cancelNativeDictation: () => Promise<unknown>;
  /** True when capture runs natively, so the waveform reads native levels. */
  readonly shouldUseNativeAudio: () => boolean;
  readonly useReadyToRequestMic: () => boolean;
  /**
   * Acquire / release the host's shared mic analyser. A port, not the direct
   * `@heidi/capture` import this row carried app-side: capture's door builds an
   * `AudioContext` at module scope, and this one is eagerly evaluated.
   */
  readonly useAudioAnalyzer: () => {
    getMicAnalyzer: () => Promise<ComposerDictationMicAnalyzer>;
    releaseMicAnalyzer: (id: string) => unknown;
  };
  /** Latest native level set, read imperatively — never a subscription. */
  readonly readNativeAudioLevels: () => readonly number[] | undefined;
  /** Hold the native audio-levels listener while `active`; nothing publishes without it. */
  readonly useAudioLevelsSubscription: (active: boolean) => void;
  /** The mic-selector's frequency-to-bar-height mapping, used on the web. */
  readonly getBarHeights: (frequencyData: Uint8Array) => readonly number[];
}

export interface ComposerDictationBarProps {
  editor: Editor | null;
  /** Resolve the live TipTap instance at read time (avoids a null first paint). */
  getEditor?: () => Editor | null;
  /** Reports whether a dictation is live so the composer can swap its editor row out. */
  onActiveChange: (active: boolean) => void;
  ports: ComposerDictationPorts;
}

/**
 * The composer's live-dictation row: cancel, a scrolling audio-reactive
 * waveform and submit, with a Listening / Transcribing status line above.
 * Renders nothing while idle; the composer keeps the mic (`DictateButton`)
 * mounted underneath, and that button inserts the transcript on completion.
 */
export const ComposerDictationBar = ({
  editor,
  getEditor,
  onActiveChange,
  ports,
}: ComposerDictationBarProps): JSX.Element | null => {
  const intl = useIntl();
  const dictation = useComposerDictation(editor, getEditor, ports);
  const shouldReduceMotion = useReducedMotion();

  // Layout effect: the composer hides its editor row on this signal, and a
  // passive effect would let the editor and this row paint together for a
  // frame, so the card would open too tall and then shrink.
  useLayoutEffect(() => {
    onActiveChange(dictation.isActive);
  }, [dictation.isActive, onActiveChange]);

  if (!dictation.isActive) return null;

  return (
    <m.div
      initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: shouldReduceMotion ? 0 : 0.2, ease: 'easeOut' }}
      className="flex min-w-0 flex-1 flex-col gap-1"
      data-testid="composer-dictation-bar"
    >
      <TypographyCaption
        aria-live="polite"
        className="text-text-tertiary"
        data-testid="composer-dictation-status"
      >
        {dictation.isProcessing
          ? intl.formatMessage({
              defaultMessage: 'Transcribing…',
              id: 'X05eg0c1g7',
              description: 'Composer dictation status while the recording is transcribed',
            })
          : intl.formatMessage({
              defaultMessage: 'Listening…',
              id: 'hB695mkGnL',
              description: 'Composer dictation status while the mic is recording',
            })}
      </TypographyCaption>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="md"
          iconOnly
          flush="start"
          onClick={dictation.cancel}
          disabled={dictation.isProcessing}
          aria-label={intl.formatMessage({
            defaultMessage: 'Cancel dictation',
            id: '5PiY7sMS1E',
            description: 'Composer dictation row: abort without transcribing',
          })}
          data-testid="composer-dictation-cancel"
          className="rounded-lg text-text-secondary"
        >
          <X aria-hidden />
        </Button>
        <ComposerDictationWaveform
          listening={dictation.isListening}
          ports={ports}
          className="min-w-0 flex-1"
        />
        <Button
          type="button"
          size="md"
          iconOnly
          onClick={dictation.submit}
          disabled={dictation.isProcessing}
          aria-label={intl.formatMessage({
            defaultMessage: 'Submit dictation',
            id: 'iJZm0IJHoQ',
            description: 'Composer dictation row: stop recording and transcribe',
          })}
          aria-busy={dictation.isProcessing || undefined}
          data-testid="composer-dictation-submit"
          className="rounded-lg transition-transform active:scale-95 motion-reduce:transform-none"
        >
          {dictation.isProcessing ? (
            <Loader2 className="animate-spin" aria-hidden />
          ) : (
            <Check aria-hidden />
          )}
        </Button>
      </div>
    </m.div>
  );
};
